import { FC, memo, useState } from 'react';

import { useMediaQuery } from '@mui/material';

import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { usePagePathParams } from '@hooks_common/usePagePathParams';

import ExternalLink from '@components_iqfu/public/external_link/ExternalLink';
import SectionLeadLeftAlign from '@components_iqfu/public/section_lead_left_align/SectionLeadLeftAlign';
// Import Swiper styles
import 'swiper/css';

import { SalesStatsSurvey } from '@type-def_iqfu/public/MarketData';

import { MediaQuery } from '@constants_common';

import styles from './SalesStatsSurveys.module.scss';

const SurveysPropertiesTypes = {
  mansion: '中古マンション',
  land: '土地',
  house: '一戸建て',
};

type Props = {
  propertyType: keyof typeof SurveysPropertiesTypes;
  surveryType: 'company' | 'seller';
  SalesStatsSurveyData: SalesStatsSurvey[];
  areaInfo: { prefecture?: string; city?: string; parent_major_city?: string; city_category?: string };
};

const SalesStatsSurveys: FC<Props> = memo(({ propertyType, surveryType, SalesStatsSurveyData, areaInfo }) => {
  const [prevEl, setPrevEl] = useState<HTMLElement | null>(null);
  const [nextEl, setNextEl] = useState<HTMLElement | null>(null);
  const isMobile = useMediaQuery(MediaQuery.lg);

  const surveryYear = '2023';
  const surveyTargetString =
    surveryType === 'company' ? '不動産屋さん' : `${SurveysPropertiesTypes[propertyType]}を売った人`;

  const desktopSlidesPerView = 2;
  return (
    <>
      {SalesStatsSurveyData && (
        <>
          <div className={styles.lead_container}>
            <SectionLeadLeftAlign>{`${surveryYear}年${surveyTargetString}へのアンケート`}</SectionLeadLeftAlign>
          </div>
          <div className={styles.swiper_container}>
            <Swiper
              modules={[Navigation]}
              navigation={{
                prevEl,
                nextEl,
              }}
              threshold={10}
              spaceBetween={8}
              slidesPerView={1}
              breakpoints={{
                992: {
                  slidesPerView: desktopSlidesPerView,
                  spaceBetween: 8,
                },
              }}
            >
              {SalesStatsSurveyData &&
                SalesStatsSurveyData.map((survey, i) => (
                  <SwiperSlide key={i}>
                    <SalesStatsSurveyCard surveyData={survey} areaInfo={areaInfo} />
                  </SwiperSlide>
                ))}
            </Swiper>
            {(isMobile || SalesStatsSurveyData?.length > desktopSlidesPerView) && (
              <>
                <div ref={(node) => setPrevEl(node)} className={styles.slider_prev}>
                  <div className={styles.icon_wrap}>
                    <img src="/images/iqfu/public/sales_stats_surveys/previous.svg" alt="prev" />
                  </div>
                </div>
                <div ref={(node) => setNextEl(node)} className={styles.slider_next}>
                  <div className={styles.icon_wrap}>
                    <img src="/images/iqfu/public/sales_stats_surveys/next.svg" alt="next" />
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
});

SalesStatsSurveys.displayName = 'SalesStatsSurveys';

const SalesStatsSurveyCard: FC<{
  surveyData: SalesStatsSurvey;
  areaInfo: { prefecture?: string; city?: string; parent_major_city?: string; city_category?: string };
}> = memo(({ surveyData, areaInfo }) => {
  const displaySurveyAreaName = () => {
    switch (surveyData.survey_type.area_type) {
      case 'country':
        return '全国';
      case 'prefecture':
        return areaInfo.prefecture ?? '';
      case 'city':
        return areaInfo.city ?? '';
      case 'major_city':
        return areaInfo.city_category === 'major' ? areaInfo.city ?? '' : areaInfo.parent_major_city ?? '';
      default:
        return '';
    }
  };

  const isMobile = useMediaQuery(MediaQuery.lg);

  const pathParam = usePagePathParams();

  const mediaLinkQueryParam = `?utm_source=iqfu&utm_medium=referral&utm_campaign=sale_stats_surveys&utm_content=${pathParam}`;

  return (
    <div className={styles.survey_card}>
      <h3 className={styles.survey_card_title}>
        {surveyData.title.value}({displaySurveyAreaName()})
      </h3>
      <ul className={styles.answer_stacks}>
        {surveyData.answers.map((answer, i) => (
          <li className={`${styles.answer_item} ${i === 0 ? styles.answer_item_first : ''}`} key={i}>
            <span className={styles.answer_ranks}>{i + 1}.</span>
            <span className={styles.answer_value}>{answer.value}</span>
            <span className={styles.answer_rate}>{answer.rate}%</span>
          </li>
        ))}
      </ul>
      {surveyData.media_url.value ? (
        <ExternalLink
          href={`${surveyData.media_url.value}${mediaLinkQueryParam}`}
          target={isMobile ? '_self' : '_blank'}
          rel="noreferrer"
        >
          <span className={styles.media_link_button}>記事を読む</span>
        </ExternalLink>
      ) : (
        <></>
      )}
    </div>
  );
});

SalesStatsSurveyCard.displayName = 'SalesStatsSurveyCard';
export default SalesStatsSurveys;
